import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Calendar, MapPin, Image as ImageIcon } from 'lucide-react';
import { MasonryGrid } from '@/components/MasonryGrid';
import { API_BASE } from '@/lib/api';
import { Toaster } from 'sonner';

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [requiresPwd, setRequiresPwd] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(-1);

  const sharePrefix = `${API_BASE}/share/${token}`;
  const buildFileUrl = (mid, thumb = false) => {
    const params = new URLSearchParams();
    if (password) params.set('password', password);
    if (thumb) params.set('thumb', 'true');
    const q = params.toString();
    return `${sharePrefix}/media/${mid}/file${q ? `?${q}` : ''}`;
  };

  const fetchShare = async (pwd) => {
    setLoading(true); setError(null);
    try {
      const url = pwd ? `${sharePrefix}?password=${encodeURIComponent(pwd)}` : sharePrefix;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) {
        setError(res.status === 404 ? 'Link not found or expired.' : 'Could not load share.');
        setData(null);
        return;
      }
      const j = await res.json();
      if (j.requires_password) {
        setRequiresPwd(true);
        setData(null);
      } else {
        setRequiresPwd(false);
        setData(j);
      }
    } catch (e) { setError('Network error.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchShare(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" data-testid="share-loading">
        <div className="h-9 w-9 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background" data-testid="share-page">
      <Toaster position="top-center" richColors />
      <header className="border-b bg-card">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/logo-icon.png" alt="Memoir" className="h-9 w-9 rounded-lg bg-white p-1 shadow border" />
          <div>
            <div className="font-serif text-lg font-semibold leading-tight">Memoir<sup className="text-[10px] align-super ml-0.5">™</sup></div>
            <div className="text-[10px] text-muted-foreground">Shared gallery · view only</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5">
        {error && (
          <Card className="rounded-2xl p-6 text-center" data-testid="share-error">
            <div className="font-serif text-xl font-semibold mb-1">Can't open this link</div>
            <p className="text-sm text-muted-foreground">{error}</p>
          </Card>
        )}

        {requiresPwd && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="rounded-2xl p-5 max-w-sm mx-auto" data-testid="share-password-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center"><Lock className="h-4 w-4" /></div>
                <div className="font-serif text-lg font-semibold">Password required</div>
              </div>
              <Label className="text-xs">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" data-testid="share-password-field" />
              <Button onClick={() => fetchShare(password)} className="w-full mt-3 rounded-xl gradient-fab text-white" data-testid="share-unlock-button">Unlock</Button>
            </Card>
          </motion.div>
        )}

        {data && data.type === 'event' && data.event && (
          <>
            <div className="mb-4" data-testid="share-event-header">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{data.event.type?.replace('_', ' ')}</div>
              <h1 className="font-serif text-3xl font-semibold leading-tight">{data.event.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                {data.event.start_date && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(data.event.start_date).toLocaleDateString()} → {new Date(data.event.end_date).toLocaleDateString()}</span>}
                {data.event.location_text && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{data.event.location_text}</span>}
              </div>
              {data.event.description && <p className="text-sm mt-2 whitespace-pre-line">{data.event.description}</p>}
            </div>
            <SharedGallery items={data.media} buildFileUrl={buildFileUrl} onOpen={setLightbox} />
          </>
        )}
        {data && data.type === 'album' && data.album && (
          <>
            <div className="mb-4" data-testid="share-album-header">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Album {data.event_title ? `from ${data.event_title}` : ''}</div>
              <h1 className="font-serif text-3xl font-semibold leading-tight">{data.album.title}</h1>
              {data.album.description && <p className="text-sm mt-1 whitespace-pre-line">{data.album.description}</p>}
            </div>
            <SharedGallery items={data.media} buildFileUrl={buildFileUrl} onOpen={setLightbox} />
          </>
        )}
        {data && data.type === 'media' && (
          <>
            <div className="mb-4" data-testid="share-media-header">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Shared photo</div>
              <h1 className="font-serif text-2xl font-semibold leading-tight">{data.media[0]?.caption || 'A moment from Memoir'}</h1>
            </div>
            <SharedGallery items={data.media} buildFileUrl={buildFileUrl} onOpen={setLightbox} />
          </>
        )}

        {lightbox >= 0 && data?.media?.[lightbox] && (
          <ShareLightbox items={data.media} index={lightbox} buildFileUrl={buildFileUrl} onClose={() => setLightbox(-1)} setIndex={setLightbox} />
        )}

        <footer className="text-center text-[11px] text-muted-foreground mt-10 mb-4">
          Memoir™ · Capture. Share. Remember.
        </footer>
      </main>
    </div>
  );
}

const SharedGallery = ({ items, buildFileUrl, onOpen }) => {
  if (!items || items.length === 0) {
    return <Card className="rounded-2xl p-6 text-center"><ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" /><div className="text-sm text-muted-foreground">No media in this share.</div></Card>;
  }
  return (
    <MasonryGrid testid="shared-gallery">
      {items.map((m, idx) => (
        <button key={m.id} onClick={() => onOpen(idx)} data-testid={`share-thumb-${m.id}`} className="block w-full overflow-hidden rounded-xl bg-secondary relative">
          <div style={{ aspectRatio: `${Math.max(0.5, Math.min(1.8, (m.width && m.height) ? m.width / m.height : 1))}` }}>
            {m.is_video ? (
              <video src={buildFileUrl(m.id)} preload="metadata" className="w-full h-full object-cover" />
            ) : (
              <img src={buildFileUrl(m.id, true)} alt={m.caption || ''} loading="lazy" className="w-full h-full object-cover" />
            )}
          </div>
        </button>
      ))}
    </MasonryGrid>
  );
};

const ShareLightbox = ({ items, index, buildFileUrl, onClose, setIndex }) => {
  const m = items[index];
  return (
    <div className="fixed inset-0 z-[10001] bg-black/95 text-white flex items-center justify-center" data-testid="share-lightbox" onClick={onClose}>
      <button className="absolute top-3 right-3 h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center" onClick={(e) => { e.stopPropagation(); onClose(); }}>✕</button>
      {index > 0 && <button className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/15" onClick={(e) => { e.stopPropagation(); setIndex(index - 1); }}>‹</button>}
      {index < items.length - 1 && <button className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/15" onClick={(e) => { e.stopPropagation(); setIndex(index + 1); }}>›</button>}
      <div className="max-w-full max-h-[100dvh]" onClick={(e) => e.stopPropagation()}>
        {m.is_video ? (
          <video controls autoPlay src={buildFileUrl(m.id)} className="max-h-[100dvh] max-w-full" />
        ) : (
          <img src={buildFileUrl(m.id)} alt="" className="max-h-[100dvh] max-w-full" />
        )}
      </div>
      <div className="absolute bottom-3 inset-x-0 text-center text-xs opacity-80">{index + 1} / {items.length} · view only</div>
    </div>
  );
};
